import { cn } from "@/lib/utils"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 btn-shine",
  {
    variants: {
      variant: {
        primary: "bg-blue text-white hover:bg-blue-dark shadow-lg shadow-blue/20",
        secondary: "bg-ink text-white hover:bg-blue",
        outline:
          "border border-border bg-white text-ink hover:border-blue-mid hover:bg-blue-light hover:text-blue",
        ghost: "text-muted hover:text-ink hover:bg-surface",
        white: "bg-white text-blue hover:bg-blue-light shadow-xl shadow-ink/20",
        "white-outline": "border border-white/30 bg-transparent text-white hover:bg-white/10",
        danger: "bg-rose-DEFAULT text-white hover:opacity-90",
      },
      size: {
        sm: "px-4 py-2 text-xs",
        md: "px-5 py-2.5",
        lg: "px-6 py-3.5",
        xl: "px-8 py-4 text-base",
      },
      rounded: {
        default: "rounded-xl",
        full: "rounded-full",
        lg: "rounded-2xl",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      rounded: "default",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, rounded, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, rounded, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)

Button.displayName = "Button"
export { Button, buttonVariants }
